/* ==========================================================================
   reveal.js — animação de entrada ao rolar (loja inteira)
   --------------------------------------------------------------------------
   Vanilla, ES5, zero dependência. NUNCA jQuery. Par de assets/reveal.css.
   Carregar com defer, depois de theme.js.

   COMO FUNCIONA
   - UM único IntersectionObserver para a loja inteira. Nada de observer por
     seção. Depois de revelar, unobserve: custo contínuo zero.
   - Variantes por atributo: data-reveal="fade|up|left|right|scale".
   - Stagger automático: quando um lote de irmãos entra junto, o JS ordena por
     posição e escreve --reveal-i (0..6) inline. Grade de cards entra em
     cascata, não em bloco.
   - Nada que já esteja visível no load é animado: no primeiro passe os
     elementos dentro da viewport são marcados como prontos e nem chegam a
     ficar escondidos. Sem flash, sem atrasar o LCP.
   - prefers-reduced-motion: reduce → o JS não esconde nada e não observa nada.
   - Conteúdo injetado depois (filtro AJAX, paginação, editor de temas) é
     varrido de novo e aí sim anima, mesmo dentro da viewport.

   AUTO-APLICAÇÃO (opcional, ligada por padrão)
     containers: .index-section > *
     itens:      .index-section .grid-item   (cards ganham a cascata; quando um
                 container tem itens, o container não é marcado, os itens são)
     opt-in extra: [data-reveal-children] > *
   Desligar pela classe no <body>:
     body.reveal-manual  → só anima o que tiver [data-reveal] escrito à mão
     body.reveal-off     → desliga o sistema inteiro

   API PÚBLICA
     window.themeReveal.scan(root)   varre um pedaço do DOM
     window.themeReveal.revealAll(root)  revela tudo que estiver pendente
   ========================================================================== */

(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  // Sem IntersectionObserver: não esconde nada. Loja visível é melhor que
  // loja animada. (Safari <12.1 / navegadores antigos.)
  if (!('IntersectionObserver' in window)) return;

  var CLASS_PENDING = 'reveal-pending';
  var CLASS_IN = 'reveal-in';
  var ATTR = 'data-reveal';
  var ATTR_STATE = 'data-reveal-state';
  var ATTR_DELAY = 'data-reveal-delay';
  var ATTR_INDEX = 'data-reveal-i';

  var DEFAULT_VARIANT = 'up';
  var VARIANTS = { fade: 1, up: 1, left: 1, right: 1, scale: 1 };

  // Teto do escalonamento: 6 passos × 70ms = 420ms de cascata, no máximo.
  var MAX_STEPS = 6;

  // Só considera "entrou" quando passou um pouco da borda de baixo.
  var IO_OPTIONS = {
    root: null,
    rootMargin: '0px 0px -8% 0px',
    threshold: 0.06
  };

  // Fatia da viewport considerada "já visível no load".
  var ABOVE_FOLD_RATIO = 0.92;

  var AUTO_CONTAINERS = '.index-section > *, [data-reveal-children] > *';
  var AUTO_ITEMS = '.index-section .grid-item';

  // Regiões que nunca entram na auto-aplicação: já têm animação própria,
  // são fixas, ou some/aparece por conta de outro script.
  var SKIP_ZONES = [
    '[data-reveal-skip]',
    '[data-reveal]',
    '.site-header',
    '.site-footer',
    '.header-wrapper',
    '.drawer',
    '.modal',
    '.newsletter-popup',
    '.slideshow-wrapper',
    '.flickity-enabled',
    '.slick-slider',
    '.flickity-viewport',
    '.slick-track',
    '[data-reveal-state]'
  ].join(',');

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  var observer = null;
  var firstPass = true;
  var mutationQueue = [];
  var mutationScheduled = false;

  /* ----------------------------------------------------------------------
     Utilidades
     ---------------------------------------------------------------------- */

  function isElement(node) {
    return !!node && node.nodeType === 1;
  }

  function prefersReduced() {
    return !!(reduceMotion && reduceMotion.matches);
  }

  function systemOff() {
    return (
      prefersReduced() ||
      (document.body && document.body.classList.contains('reveal-off'))
    );
  }

  function autoEnabled() {
    if (!document.body) return false;
    if (document.body.classList.contains('reveal-manual')) return false;
    if (document.body.classList.contains('reveal-off')) return false;
    return document.body.getAttribute('data-reveal-auto') !== 'off';
  }

  function closestMatch(el, selector) {
    if (el.closest) return el.closest(selector);
    var node = el;
    while (node && node.nodeType === 1) {
      if (node.matches && node.matches(selector)) return node;
      node = node.parentElement;
    }
    return null;
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function normalizeVariant(value) {
    var v = (value || '').toString().trim().toLowerCase();
    if (v && VARIANTS[v]) return v;
    return DEFAULT_VARIANT;
  }

  // Já visível no primeiro paint? Então não entra no jogo.
  function inViewportNow(rect) {
    var vh = window.innerHeight || document.documentElement.clientHeight;
    if (rect.width === 0 && rect.height === 0) return true; // display:none / vazio
    return rect.top < vh * ABOVE_FOLD_RATIO && rect.bottom > 0;
  }

  /* ----------------------------------------------------------------------
     Revelar
     ---------------------------------------------------------------------- */

  function markReady(el) {
    el.setAttribute(ATTR_STATE, 'ready');
  }

  function reveal(el, index) {
    if (el.getAttribute(ATTR_DELAY)) {
      var ms = parseInt(el.getAttribute(ATTR_DELAY), 10);
      if (!isNaN(ms)) el.style.setProperty('--reveal-delay', ms + 'ms');
    } else {
      var manual = parseInt(el.getAttribute(ATTR_INDEX), 10);
      var i = !isNaN(manual) ? manual : index || 0;
      if (i > MAX_STEPS) i = MAX_STEPS;
      if (i > 0) el.style.setProperty('--reveal-i', String(i));
    }

    el.classList.remove(CLASS_PENDING);
    el.classList.add(CLASS_IN);
    markReady(el);
  }

  // Lote que entrou junto: ordena por posição e escalona por pai.
  // Cada grupo de irmãos recomeça do zero, então duas colunas lado a lado
  // cascateiam em paralelo em vez de somar atraso.
  function handleEntries(entries, obs) {
    var visible = [];
    var i;

    for (i = 0; i < entries.length; i++) {
      if (!entries[i].isIntersecting) continue;
      visible.push(entries[i]);
      obs.unobserve(entries[i].target);
    }

    if (!visible.length) return;

    visible.sort(function (a, b) {
      var ra = a.boundingClientRect;
      var rb = b.boundingClientRect;
      if (ra.top !== rb.top) return ra.top - rb.top;
      return ra.left - rb.left;
    });

    var parents = [];
    var counts = [];

    for (i = 0; i < visible.length; i++) {
      var el = visible[i].target;
      var parent = el.parentElement;
      var slot = parents.indexOf(parent);

      if (slot === -1) {
        parents.push(parent);
        counts.push(0);
        slot = parents.length - 1;
      }

      reveal(el, counts[slot]);
      counts[slot] = counts[slot] + 1;
    }
  }

  function getObserver() {
    if (!observer) observer = new IntersectionObserver(handleEntries, IO_OPTIONS);
    return observer;
  }

  /* ----------------------------------------------------------------------
     Varredura
     ---------------------------------------------------------------------- */

  function isCandidate(el) {
    if (!isElement(el)) return false;
    if (el.getAttribute(ATTR_STATE)) return false;
    if (el.classList.contains(CLASS_PENDING)) return false;
    if (el.classList.contains(CLASS_IN)) return false;
    return true;
  }

  function collectExplicit(root, out) {
    if (isElement(root) && root.hasAttribute(ATTR) && isCandidate(root)) {
      out.push(root);
    }
    var found = root.querySelectorAll ? root.querySelectorAll('[' + ATTR + ']') : [];
    for (var i = 0; i < found.length; i++) {
      if (isCandidate(found[i])) out.push(found[i]);
    }
  }

  function collectAuto(root, out) {
    if (!autoEnabled() || !root.querySelectorAll) return;

    var items = toArray(root.querySelectorAll(AUTO_ITEMS)).filter(function (el) {
      return isCandidate(el) && !closestMatch(el, SKIP_ZONES);
    });

    var containers = toArray(root.querySelectorAll(AUTO_CONTAINERS)).filter(function (el) {
      if (!isCandidate(el) || closestMatch(el, SKIP_ZONES)) return false;
      // Container que embrulha cards cede a vez: a cascata é dos cards.
      for (var i = 0; i < items.length; i++) {
        if (el === items[i] || el.contains(items[i])) return false;
      }
      return true;
    });

    for (var c = 0; c < containers.length; c++) out.push(containers[c]);
    for (var t = 0; t < items.length; t++) out.push(items[t]);
  }

  function scan(root) {
    if (systemOff()) return;

    var target = root || document.body;
    if (!isElement(target)) return;

    var candidates = [];
    collectExplicit(target, candidates);
    collectAuto(target, candidates);
    if (!candidates.length) return;

    // Leitura em bloco (layout uma vez só), escrita em bloco depois.
    var rects = [];
    var i;
    for (i = 0; i < candidates.length; i++) {
      rects.push(candidates[i].getBoundingClientRect());
    }

    var io = getObserver();

    for (i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (el.getAttribute(ATTR_STATE)) continue;

      if (!el.hasAttribute(ATTR)) {
        el.setAttribute(ATTR, DEFAULT_VARIANT);
      } else {
        el.setAttribute(ATTR, normalizeVariant(el.getAttribute(ATTR)));
      }

      // No primeiro passe, o que já está na tela nem entra: fica visível como
      // veio do servidor. Depois do load, conteúdo novo anima mesmo na dobra.
      if (firstPass && inViewportNow(rects[i])) {
        markReady(el);
        continue;
      }

      el.setAttribute(ATTR_STATE, 'pending');
      el.classList.add(CLASS_PENDING);
      io.observe(el);
    }
  }

  function revealAll(root) {
    var target = root || document;
    var pending = target.querySelectorAll('.' + CLASS_PENDING);
    for (var i = 0; i < pending.length; i++) {
      if (observer) observer.unobserve(pending[i]);
      reveal(pending[i], 0);
    }
  }

  /* ----------------------------------------------------------------------
     Conteúdo dinâmico: filtro AJAX, paginação, quick add, editor de temas
     ---------------------------------------------------------------------- */

  function flushMutations() {
    mutationScheduled = false;
    var queue = mutationQueue;
    mutationQueue = [];
    for (var i = 0; i < queue.length; i++) {
      if (queue[i].isConnected !== false) scan(queue[i]);
    }
  }

  function watchDom() {
    if (!('MutationObserver' in window) || !document.body) return;

    var mo = new MutationObserver(function (mutations) {
      for (var m = 0; m < mutations.length; m++) {
        var added = mutations[m].addedNodes;
        for (var n = 0; n < added.length; n++) {
          if (!isElement(added[n])) continue;
          mutationQueue.push(added[n]);
        }
      }

      if (mutationQueue.length && !mutationScheduled) {
        mutationScheduled = true;
        window.requestAnimationFrame(flushMutations);
      }
    });

    mo.observe(document.body, { childList: true, subtree: true });
  }

  /* ----------------------------------------------------------------------
     Boot
     ---------------------------------------------------------------------- */

  function init() {
    if (systemOff()) return;

    scan(document.body);
    firstPass = false;
    watchDom();

    // Editor de temas: seção re-renderizada volta a animar; seção selecionada
    // aparece na hora, o merchant não pode ficar olhando pra bloco escondido.
    document.addEventListener('shopify:section:load', function (e) {
      scan(e.target);
    });
    document.addEventListener('shopify:section:select', function (e) {
      revealAll(e.target);
    });
    document.addEventListener('shopify:block:select', function (e) {
      revealAll(e.target);
    });

    // Se o usuário ligar "reduzir movimento" no meio da navegação,
    // devolve tudo na hora (o CSS já cobre, isto solta o observer).
    if (reduceMotion) {
      var onChange = function () {
        if (reduceMotion.matches) {
          revealAll(document);
          if (observer) observer.disconnect();
        }
      };
      if (reduceMotion.addEventListener) reduceMotion.addEventListener('change', onChange);
      else if (reduceMotion.addListener) reduceMotion.addListener(onChange);
    }
  }

  window.themeReveal = {
    scan: scan,
    revealAll: revealAll
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
